import { FC } from 'react';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import FeatureCard from './FeatureCard';
import { Colors, SECTION_PADDING } from '../../styles/theme';
import SectionTitle from '../section-title';

const features = [
  {
    title: 'One-tap Photo Capture',
    description:
      'Open the Haist app and simply point your phone at a license plate or any problem area. Capture clear photos in seconds. No manual input. No dropdowns. No wasted time.',
    image: '/images/img-features1.png',
    tabletImage: '/images/img-features1-tablet.png',
    mobileImage: '/images/img-features1-mobile.png',
  },
  {
    title: 'Real-Time AI Visual Analysis',
    description:
      'Automatically detects license plate, car model, and VIN. Haist compiles a professional report with detected issues, recommended fixes, estimated labor time, visual highlights, and past service history.',
    image: '/images/img-features2.png',
    tabletImage: '/images/img-features2-tablet.png',
    mobileImage: '/images/img-features2-mobile.png',
  },
  {
    title: 'Smart Vehicle History Lookup',
    description:
      'Instantly surfaces service history, past repairs, and any recall alerts. Sends smart notifications about warranty status or reported issues.',
    image: '/images/img-features3.png',
    tabletImage: '/images/img-features3-tablet.png',
    mobileImage: '/images/img-features3-mobile.png',
  },
  {
    title: 'Instant Customer Communication',
    description:
      'Preview the AI-generated report and send via SMS or WhatsApp. Customers get a clear breakdown of the issues and next steps.',
    image: '/images/img-features4.png',
    tabletImage: '/images/img-features4-tablet.png',
    mobileImage: '/images/img-features4-mobile.png',
  },
  {
    title: 'Boosted Productivity and Accuracy',
    description:
      'Ditch handwritten notes and unreliable manual drawings. Identifies unique problem indicators for each car make and model.',
    image: '/images/img-features5.png',
    tabletImage: '/images/img-features5-tablet.png',
    mobileImage: '/images/img-features5-mobile.png',
    fullWidth: true,
  },
];

function getFeatureImage(
  feature: { image: string; tabletImage: string; mobileImage: string },
  isMobile: boolean,
  isTablet: boolean,
): string {
  if (isMobile) return feature.mobileImage;
  if (isTablet) return feature.tabletImage;
  return feature.image;
}

const FeaturesSection: FC = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: SECTION_PADDING.py,
        px: SECTION_PADDING.px,
      }}
      id={'feature'}
    >
      <Box
        sx={{
          marginBottom: SECTION_PADDING.py,
        }}
      >
        <SectionTitle title={'Discover'} subtitle={'Haist Features'} />
      </Box>
      <Box
        sx={{
          maxWidth: '1440px',
          justifyContent: 'center',
          alignItems: 'stretch',
          margin: 0,
        }}
      >
        <Grid container spacing={{ xs: 2, md: '24px' }}>
          {features.map((f, i) => (
            <Grid item xs={12} md={f.fullWidth ? 12 : 6} key={i}>
              <FeatureCard
                {...f}
                image={getFeatureImage(f, isMobile, isTablet)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default FeaturesSection;
